import { ExistingOrderFoundBanner } from '@/components/order/ExistingOrderFoundBanner'
import {
  findExistingPatientMatch,
  type ExistingPatientMatch,
} from '@/services/existingPatientMatch'
import { Button } from '@helix-ds/react'
import { ChevronDown, Close, User } from 'flowbite-react-icons/outline'
import {
  checkProbandMrnAvailable,
  MRN_ALREADY_ASSIGNED_MESSAGE,
  type MrnOrgResolutionContext,
} from '@/services/mrnOrganizationLookup'
import {
  PatientDemographicsValidationError,
  validatePatientDemographics,
} from '@/services/patientDemographicsValidation'
import { Sex } from '@/types/RequisitionFormTypes'
import {
  MRN_INVALID_MESSAGE,
  MRN_MAX_LENGTH,
  MRN_TOO_LONG_MESSAGE,
  NAME_INVALID_MESSAGE,
  NAME_MAX_LENGTH,
  NAME_TOO_LONG_MESSAGE,
  isValidMrn,
  isValidName,
  isWithinLength,
  toProperCaseName,
} from '@/utils/freeTextValidation'
import { comparatorDobError, localTodayIso } from '@/utils/patientDob'
import { FieldMarker, FieldRequiredRail } from '@/components/common/FieldMarkers'
import { resolveFieldMarkerState } from '@/components/common/fieldMarkerState'
import React, { useEffect, useRef, useState } from 'react'

export type NewOrderModalVariant = 'create' | 'edit'

/** Matches the debounce used by the org NPI availability check. */
const EXISTING_MATCH_DEBOUNCE_MS = 400

/** Proband-only: organizational MRN uniqueness (comparators use a different UI). */
export interface ProbandMrnUniquenessContext {
  /** Loaded MRN when the edit modal opened; unchanged value skips the duplicate lookup. */
  initialMrn?: string | null
  /** IMDB patient / family-member id for this proband when known (UUID string). */
  currentImdbPatientId?: string | null
}

interface NewOrderModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (orderData: OrderFormData) => void
  userFirstName?: string
  userLastName?: string
  userEmail?: string
  /** IMDB organization id from the JWT / order context; when set, MRN is checked via patient-by-mrn. */
  organizationId?: string
  /** Optional proband edit context for MRN duplicate handling (see `checkProbandMrnAvailable`). */
  mrnUniquenessContext?: ProbandMrnUniquenessContext
  /** When `organizationId` is not an IMDB UUID, pass Epic / JWT context so the org id can be resolved. */
  mrnOrgResolution?: MrnOrgResolutionContext
  /**
   * MRNs of other people in this order (biological parents) that the proband MRN must not collide
   * with. Compared case-insensitively after trimming.
   */
  otherMrns?: ReadonlyArray<{ label: string; mrn: string | null | undefined }>
  /** `edit` reuses the same form for updating patient demographics before submit (e.g. on Order Start). */
  variant?: NewOrderModalVariant
  /** Required when `variant` is `edit` — seeds fields when the modal opens. */
  editInitialValues?: OrderFormData
  /** Dialog title (defaults by variant). */
  title?: string
  /** Primary submit action label (defaults by variant). */
  submitLabel?: string
  /**
   * GEN-5738: when true, the modal renders a REQUIRED ordering-site dropdown and
   * blocks submit until a valid site is chosen. Off (default) for orgs that do
   * not track ordering sites — the modal is unchanged for them.
   */
  siteTrackingEnabled?: boolean
  /** Selectable ordering-site names (from `ui_config.organizationSiteInformation`). */
  siteOptions?: ReadonlyArray<string>
  /**
   * Opens the existing order a duplicate warning points at. When omitted the
   * modal does not look for duplicates at all, which keeps the `edit` variant
   * (where the patient is already known) unchanged.
   */
  onOpenExistingOrder?: (match: ExistingPatientMatch) => void
}

export interface OrderFormData {
  patientLastName: string
  patientFirstName: string
  dateOfBirth: string
  mrn: string
  sex: Sex
  orderingPhysician: string
  /**
   * GEN-5738: chosen organization ordering site (a `siteName` string). Only used
   * when the org tracks ordering sites; empty string when tracking is off or no
   * pick has been made. The parent gates create/submit on a valid selection.
   */
  orderingSite?: string
}

/**
 * New Order Modal component
 * Modal for creating a new genetic testing order
 */
export const NewOrderModal: React.FC<NewOrderModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userFirstName,
  userLastName,
  userEmail,
  organizationId,
  mrnUniquenessContext,
  mrnOrgResolution,
  otherMrns,
  variant = 'create',
  editInitialValues,
  title,
  submitLabel,
  siteTrackingEnabled = false,
  siteOptions,
  onOpenExistingOrder,
}) => {
  const defaultOrderingPhysician =
    `${userFirstName || ''} ${userLastName || ''}`.trim() || userEmail || ''

  const [formData, setFormData] = useState<OrderFormData>({
    patientLastName: '',
    patientFirstName: '',
    dateOfBirth: '',
    mrn: '',
    sex: Sex.Unknown,
    orderingPhysician: defaultOrderingPhysician,
    orderingSite: '',
  })
  const [isValidating, setIsValidating] = useState(false)
  const [dobBadInput, setDobBadInput] = useState(false)
  const [serverValidationError, setServerValidationError] = useState<string | null>(null)
  const [existingMatch, setExistingMatch] = useState<ExistingPatientMatch | null>(null)
  /** Set when user cancels during validation; prevents onSubmit from running after modal closed */
  const submitCancelledRef = useRef(false)
  /** Only seed the form when the dialog opens (avoids clobbering in-progress edits on parent re-render). */
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return
    setServerValidationError(null)
    setDobBadInput(false)
    setExistingMatch(null)
    if (variant === 'edit' && editInitialValues) {
      setFormData(editInitialValues)
      return
    }
    setFormData({
      patientLastName: '',
      patientFirstName: '',
      dateOfBirth: '',
      mrn: '',
      sex: Sex.Unknown,
      orderingPhysician: defaultOrderingPhysician,
      orderingSite: '',
    })
  }, [isOpen, variant, editInitialValues, defaultOrderingPhysician])

  const firstNameCharInvalid =
    formData.patientFirstName.length > 0 && !isValidName(formData.patientFirstName)
  const firstNameTooLong = !isWithinLength(formData.patientFirstName, NAME_MAX_LENGTH)
  const firstNameInvalid = firstNameCharInvalid || firstNameTooLong
  const firstNameError = firstNameCharInvalid
    ? NAME_INVALID_MESSAGE
    : firstNameTooLong
      ? NAME_TOO_LONG_MESSAGE
      : undefined
  const lastNameCharInvalid =
    formData.patientLastName.length > 0 && !isValidName(formData.patientLastName)
  const lastNameTooLong = !isWithinLength(formData.patientLastName, NAME_MAX_LENGTH)
  const lastNameInvalid = lastNameCharInvalid || lastNameTooLong
  const lastNameError = lastNameCharInvalid
    ? NAME_INVALID_MESSAGE
    : lastNameTooLong
      ? NAME_TOO_LONG_MESSAGE
      : undefined
  const namesValid = !firstNameInvalid && !lastNameInvalid
  const mrnCharInvalid = formData.mrn.length > 0 && !isValidMrn(formData.mrn)
  const mrnTooLong = !isWithinLength(formData.mrn, MRN_MAX_LENGTH)
  const mrnDuplicate = (() => {
    const thisMrn = formData.mrn.trim()
    if (!thisMrn || !otherMrns?.length) {
      return undefined
    }
    const normalized = thisMrn.toLowerCase()
    const conflict = otherMrns.find((other) => other.mrn?.trim().toLowerCase() === normalized)
    return conflict ? `MRN must be different from the ${conflict.label}'s MRN` : undefined
  })()
  const mrnInvalid = mrnCharInvalid || mrnTooLong || Boolean(mrnDuplicate)
  const mrnErrorMessage = mrnCharInvalid
    ? MRN_INVALID_MESSAGE
    : mrnTooLong
      ? MRN_TOO_LONG_MESSAGE
      : mrnDuplicate
  const dobError = dobBadInput
    ? 'Date of birth must be a valid date'
    : formData.dateOfBirth.length > 0
      ? comparatorDobError(formData.dateOfBirth)
      : undefined

  // GEN-5738: required ordering-site selection (only when the org tracks sites).
  // Valid = a non-blank pick that is still in the configured allowlist.
  const siteOptionList = siteOptions ?? []
  const orderingSiteValue = formData.orderingSite ?? ''
  // Blocks submit: any blank or out-of-allowlist selection while tracking is on.
  const siteInvalid = siteTrackingEnabled && !siteOptionList.includes(orderingSiteValue)
  // Drives the red/error styling: ONLY a non-blank pick that isn't in the
  // allowlist (a stale config). The initial empty state is a normal "not yet
  // chosen" field — not an error — so it renders neutrally, like the sex field.
  const siteShowInvalidStyle =
    siteTrackingEnabled && orderingSiteValue.length > 0 && !siteOptionList.includes(orderingSiteValue)
  // Only surface an inline error for a non-blank-but-invalid pick (a stale
  // allowlist). The blank-required case is conveyed by the disabled submit +
  // required marker, matching how the sex field behaves.
  const siteError = siteShowInvalidStyle
    ? 'Selected ordering site is not valid for this organization.'
    : undefined

  const detectionEnabled = Boolean(onOpenExistingOrder)
  /**
   * Blocks submit so the prompt cannot be typed past into a duplicate.
   *
   * Safe to gate on because the prompt is always dismissible: the match is
   * keyed on the MRN when one is entered, so changing it to an unused MRN
   * clears the banner and re-enables submit. A name+DOB match only occurs
   * while the MRN is blank, and entering one takes over detection.
   */
  const hasExistingPatientMatch = detectionEnabled && Boolean(existingMatch)
  const mrnForDetection = mrnInvalid ? '' : formData.mrn.trim()
  const dobForDetection = dobError ? '' : formData.dateOfBirth.trim()
  const firstNameForDetection = namesValid ? formData.patientFirstName.trim() : ''
  const lastNameForDetection = namesValid ? formData.patientLastName.trim() : ''
  const orgIdForDetection = (organizationId ?? '').trim()

  /**
   * Look for an existing patient while the provider types, so the duplicate is
   * visible before submit rather than as a rejection afterwards.
   *
   * Debounced and abortable: every keystroke would otherwise issue a PHI-bearing
   * request, and out-of-order replies could show a banner for an MRN the
   * provider has already corrected.
   */
  useEffect(() => {
    if (!isOpen || !detectionEnabled) return
    if (!orgIdForDetection && !mrnOrgResolution) return

    const hasMrn = Boolean(mrnForDetection)
    const hasNameDob = Boolean(firstNameForDetection && lastNameForDetection && dobForDetection)
    if (!hasMrn && !hasNameDob) {
      setExistingMatch(null)
      return
    }

    const controller = new AbortController()
    const timer = setTimeout(() => {
      void findExistingPatientMatch({
        organizationId: orgIdForDetection,
        mrn: mrnForDetection,
        firstName: firstNameForDetection,
        lastName: lastNameForDetection,
        dateOfBirth: dobForDetection,
        signal: controller.signal,
        orgResolution: mrnOrgResolution,
      })
        .then((match) => {
          if (controller.signal.aborted) return
          setExistingMatch(match)
        })
        .catch(() => {
          // A failed detection must never block ordering; submit-time
          // uniqueness remains the authoritative check. The banner is still
          // cleared, because leaving a previous match on screen would present
          // a stale patient as the current one. Aborts are skipped: a newer
          // lookup is already in flight and would only flicker the banner.
          if (controller.signal.aborted) return
          setExistingMatch(null)
        })
    }, EXISTING_MATCH_DEBOUNCE_MS)

    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [
    isOpen,
    detectionEnabled,
    orgIdForDetection,
    mrnForDetection,
    firstNameForDetection,
    lastNameForDetection,
    dobForDetection,
    mrnOrgResolution,
  ])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!namesValid || mrnInvalid || Boolean(dobError) || siteInvalid || hasExistingPatientMatch) return
    setServerValidationError(null)
    submitCancelledRef.current = false
    setIsValidating(true)
    const trimmed: OrderFormData = {
      ...formData,
      patientFirstName: toProperCaseName(formData.patientFirstName),
      patientLastName: toProperCaseName(formData.patientLastName),
      mrn: formData.mrn.trim(),
    }
    try {
      const result = await validatePatientDemographics({
        name: { first: trimmed.patientFirstName, last: trimmed.patientLastName },
        dateOfBirth: trimmed.dateOfBirth,
        MRN: trimmed.mrn,
      })
      if (submitCancelledRef.current) return
      if (!result.valid) {
        setServerValidationError('Patient demographics did not pass validation.')
        return
      }

      if (organizationId?.trim() || mrnOrgResolution) {
        const mrnUnique = await checkProbandMrnAvailable({
          organizationId: (organizationId ?? '').trim(),
          mrn: trimmed.mrn,
          skipLookupWhenMrnUnchangedFrom:
            variant === 'edit' ? mrnUniquenessContext?.initialMrn : undefined,
          currentImdbPatientId: mrnUniquenessContext?.currentImdbPatientId,
          orgResolution: mrnOrgResolution,
        })
        if (submitCancelledRef.current) return
        if (!mrnUnique.ok) {
          setServerValidationError(mrnUnique.message)
          return
        }
      }

      // C2: Marker clearing moved to call sites (Orders.tsx for standalone, OrderStart.tsx for Epic)
      onSubmit(trimmed)
      if (variant !== 'edit') {
        setFormData({
          patientLastName: '',
          patientFirstName: '',
          dateOfBirth: '',
          mrn: '',
          sex: Sex.Unknown,
          orderingPhysician: '',
        })
      }
    } catch (err) {
      if (submitCancelledRef.current) return
      if (err instanceof PatientDemographicsValidationError) {
        setServerValidationError(err.message)
      } else {
        setServerValidationError("Couldn't validate patient details. Try again.")
      }
    } finally {
      setIsValidating(false)
    }
  }

  const handleCancel = () => {
    submitCancelledRef.current = true
    setServerValidationError(null)
    if (variant === 'edit' && editInitialValues) {
      setFormData(editInitialValues)
    } else {
      setFormData({
        patientLastName: '',
        patientFirstName: '',
        dateOfBirth: '',
        mrn: '',
        sex: Sex.Unknown,
        orderingPhysician: '',
      })
    }
    onClose()
  }

  if (!isOpen) return null

  const headingTitle =
    title ?? (variant === 'edit' ? 'Patient information' : 'New Order')
  const primarySubmitLabel =
    submitLabel ?? (variant === 'edit' ? 'Save' : 'Create')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleCancel} aria-hidden="true" />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-lg rounded-base border border-border-base bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-900">
        {/* Header */}
        <div className="mb-6 border-b border-border-base pb-5 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-text-heading dark:text-white">{headingTitle}</h2>
            <button onClick={handleCancel} className="text-text-subtle hover:text-text-body dark:text-gray-500 dark:hover:text-gray-400" aria-label="Close modal">
              <Close className="h-6 w-6" />
            </button>
          </div>
          <h3 className="mt-2 text-sm font-semibold text-text-heading dark:text-white">Patient Demographics</h3>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Patient First and Last Name Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Patient First Name */}
            <div className="flex items-stretch gap-2">
              <FieldRequiredRail
                state={resolveFieldMarkerState(
                  true,
                  formData.patientFirstName.trim().length > 0 && !firstNameInvalid
                )}
              />
              <div className="min-w-0 flex-1">
              <label className="mb-2 flex items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
                First Name
                <FieldMarker
                  state={resolveFieldMarkerState(
                    true,
                    formData.patientFirstName.trim().length > 0 && !firstNameInvalid
                  )}
                  fieldLabel="First Name"
                />
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="h-5 w-5 text-text-subtle dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  required
                  maxLength={NAME_MAX_LENGTH}
                  value={formData.patientFirstName}
                  onChange={(e) => {
                    setServerValidationError(null)
                    setFormData({ ...formData, patientFirstName: e.target.value })
                  }}
                  placeholder="First"
                  className={`block w-full rounded-base border py-2.5 pl-10 pr-3 text-sm shadow-sm focus:outline-none focus:ring-1 ${
                    firstNameInvalid
                      ? 'border-border-error bg-error-50/50 focus:border-border-error focus:ring-error-500 dark:border-error-700 dark:bg-gray-900 dark:focus:border-error-700'
                      : 'border-border-base-medium bg-background-secondary-medium focus:border-brand-600 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-800 dark:focus:border-brand-500'
                  }`}
                />
              </div>
              {firstNameInvalid && (
                <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{firstNameError}</p>
              )}
              </div>
            </div>

            {/* Patient Last Name */}
            <div className="flex items-stretch gap-2">
              <FieldRequiredRail
                state={resolveFieldMarkerState(
                  true,
                  formData.patientLastName.trim().length > 0 && !lastNameInvalid
                )}
              />
              <div className="min-w-0 flex-1">
              <label className="mb-2 flex items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
                Last Name
                <FieldMarker
                  state={resolveFieldMarkerState(
                    true,
                    formData.patientLastName.trim().length > 0 && !lastNameInvalid
                  )}
                  fieldLabel="Last Name"
                />
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <User className="h-5 w-5 text-text-subtle dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  required
                  maxLength={NAME_MAX_LENGTH}
                  value={formData.patientLastName}
                  onChange={(e) => {
                    setServerValidationError(null)
                    setFormData({ ...formData, patientLastName: e.target.value })
                  }}
                  placeholder="Last"
                  className={`block w-full rounded-base border py-2.5 pl-10 pr-3 text-sm shadow-sm focus:outline-none focus:ring-1 ${
                    lastNameInvalid
                      ? 'border-border-error bg-error-50/50 focus:border-border-error focus:ring-error-500 dark:border-error-700 dark:bg-gray-900 dark:focus:border-error-700'
                      : 'border-border-base-medium bg-background-secondary-medium focus:border-brand-600 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-800 dark:focus:border-brand-500'
                  }`}
                />
              </div>
              {lastNameInvalid && (
                <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{lastNameError}</p>
              )}
              </div>
            </div>
          </div>

          {/* Date of Birth and MRN Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Date of Birth */}
            <div className="flex items-stretch gap-2">
              <FieldRequiredRail
                state={resolveFieldMarkerState(true, formData.dateOfBirth.length > 0 && !dobError)}
              />
              <div className="min-w-0 flex-1">
              <label className="mb-2 flex items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
                Date of Birth
                <FieldMarker
                  state={resolveFieldMarkerState(true, formData.dateOfBirth.length > 0 && !dobError)}
                  fieldLabel="Date of Birth"
                />
              </label>
              <input
                type="date"
                required
                max={localTodayIso()}
                value={formData.dateOfBirth}
                onChange={(e) => {
                  setServerValidationError(null)
                  setDobBadInput(e.target.validity.badInput)
                  setFormData({ ...formData, dateOfBirth: e.target.value })
                }}
                placeholder="MM/DD/YYYY"
                className={`block w-full rounded-base border px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-1 ${
                  dobError
                    ? 'border-border-error bg-error-50/50 focus:border-border-error focus:ring-error-500 dark:border-error-700 dark:bg-gray-900 dark:focus:border-error-700'
                    : 'border-border-base-medium bg-background-secondary-medium focus:border-brand-600 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-800 dark:focus:border-brand-500'
                }`}
              />
              {dobError && (
                <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{dobError}</p>
              )}
              </div>
            </div>

            {/* MRN */}
            <div className="flex items-stretch gap-2">
              <FieldRequiredRail
                state={resolveFieldMarkerState(true, formData.mrn.trim().length > 0 && !mrnInvalid)}
              />
              <div className="min-w-0 flex-1">
              <label className="mb-2 flex items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
                MRN
                <FieldMarker
                  state={resolveFieldMarkerState(true, formData.mrn.trim().length > 0 && !mrnInvalid)}
                  fieldLabel="MRN"
                />
              </label>
              <input
                type="text"
                required
                maxLength={MRN_MAX_LENGTH}
                value={formData.mrn}
                onChange={(e) => {
                  setServerValidationError(null)
                  setFormData({ ...formData, mrn: e.target.value })
                }}
                placeholder="#"
                aria-invalid={mrnInvalid}
                className={`block w-full rounded-base border px-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-1 ${
                  mrnInvalid
                    ? 'border-border-error bg-error-50/50 focus:border-border-error focus:ring-error-500 dark:border-error-700 dark:bg-gray-900 dark:focus:border-error-700'
                    : 'border-border-base-medium bg-background-secondary-medium focus:border-brand-600 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-800 dark:focus:border-brand-500'
                }`}
              />
              {mrnInvalid && <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{mrnErrorMessage}</p>}
              </div>
            </div>
          </div>

          {/* Assigned Sex at Birth */}
          <div className="flex items-stretch gap-2">
            <FieldRequiredRail state={resolveFieldMarkerState(true, formData.sex !== Sex.Unknown)} />
            <div className="min-w-0 flex-1">
            <label className="mb-2 flex items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
              Assigned Sex at Birth
              <FieldMarker
                state={resolveFieldMarkerState(true, formData.sex !== Sex.Unknown)}
                fieldLabel="Assigned Sex at Birth"
              />
            </label>
            <div className="relative">
              <select
                required
                value={formData.sex}
                onChange={(e) => setFormData({ ...formData, sex: e.target.value as Sex })}
                className="block w-full appearance-none rounded-base border border-border-base-medium bg-background-secondary-medium py-2.5 pl-3 pr-10 text-sm shadow-sm focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-800 dark:focus:border-brand-500"
              >
                <option value={Sex.Unknown}>Select assigned sex at birth</option>
                <option value={Sex.Male}>Male</option>
                <option value={Sex.Female}>Female</option>
                <option value={Sex.Ambiguous}>Ambiguous</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                <ChevronDown className="h-5 w-5 text-text-subtle dark:text-gray-500" />
              </div>
            </div>
            </div>
          </div>

          {/* Ordering Site (GEN-5738) — only when the org tracks ordering sites */}
          {siteTrackingEnabled && (
            <div className="flex items-stretch gap-2">
              <FieldRequiredRail
                state={resolveFieldMarkerState(true, orderingSiteValue.length > 0 && !siteInvalid)}
              />
              <div className="min-w-0 flex-1">
                <label className="mb-2 flex items-center gap-1 text-sm font-medium text-text-heading dark:text-white">
                  Ordering Site
                  <FieldMarker
                    state={resolveFieldMarkerState(true, orderingSiteValue.length > 0 && !siteInvalid)}
                    fieldLabel="Ordering Site"
                  />
                </label>
                <div className="relative">
                  <select
                    required
                    aria-invalid={siteShowInvalidStyle}
                    value={orderingSiteValue}
                    onChange={(e) => setFormData({ ...formData, orderingSite: e.target.value })}
                    className={`block w-full appearance-none rounded-base border py-2.5 pl-3 pr-10 text-sm shadow-sm focus:outline-none focus:ring-1 ${
                      siteShowInvalidStyle
                        ? 'border-border-error bg-error-50/50 focus:border-border-error focus:ring-error-500 dark:border-error-700 dark:bg-gray-900 dark:focus:border-error-700'
                        : 'border-border-base-medium bg-background-secondary-medium focus:border-brand-600 focus:ring-brand-600 dark:border-gray-700 dark:bg-gray-800 dark:focus:border-brand-500'
                    }`}
                  >
                    <option value="">Select an ordering site</option>
                    {siteOptionList.map((siteName) => (
                      <option key={siteName} value={siteName}>
                        {siteName}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronDown className="h-5 w-5 text-text-subtle dark:text-gray-500" />
                  </div>
                </div>
                {siteError && <p className="mt-1 text-xs text-text-fg-error dark:text-error-100">{siteError}</p>}
              </div>
            </div>
          )}

          {/* Existing patient in this organization: offer the order, never a duplicate. */}
          {existingMatch && onOpenExistingOrder && (
            <ExistingOrderFoundBanner
              match={existingMatch}
              onOpenExistingOrder={() => onOpenExistingOrder(existingMatch)}
            />
          )}

          {/* Server validation error. Only the duplicate-MRN message is
              suppressed while the banner is up, since the banner carries that
              exact finding with more detail and an action. Every other submit
              failure must still be shown. */}
          {serverValidationError &&
            !(existingMatch && serverValidationError === MRN_ALREADY_ASSIGNED_MESSAGE) && (
              <div className="rounded-base border border-border-error-subtle bg-error-50 px-3 py-2.5 text-sm text-text-fg-error dark:border-error-700 dark:bg-gray-900 dark:text-error-100">
                {serverValidationError}
              </div>
            )}

          {/* Footer */}
          <div className="flex gap-4 border-t border-border-base pt-6 dark:border-gray-700">
            <Button
              type="submit"
              variant="primary"
              loading={isValidating}
              disabled={
                !namesValid ||
                mrnInvalid ||
                Boolean(dobError) ||
                siteInvalid ||
                hasExistingPatientMatch ||
                isValidating
              }
              className="flex-1"
            >
              {isValidating ? 'Validating…' : primarySubmitLabel}
            </Button>
            <Button type="button" variant="secondary" onClick={handleCancel} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default NewOrderModal
